import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer, Legend,
  ReferenceLine, ComposedChart, ReferenceArea
} from 'recharts';
import { ScatterPlot, ComboChart, GaugeChart, FunnelChartViz, WaterfallChart, TreemapChart, HeatmapChart, GeoMapChart, MapLibreGeoChart, SankeyChart, PackedBubbleChart } from '@/components/AdvancedCharts';
import { PALETTES, TT_STYLE, TT_LABEL_STYLE, TT_ITEM_STYLE, AXIS_PROPS } from './constants';
import { getFieldLabel } from '@/lib/fieldLabel';
import { unitAxisSuffix } from '@/lib/units';
import { computeForecastValues, computeForecastCI } from './forecastUtils';
import { getRegisteredVizType } from '@/lib/customVizRegistry';
import WordCloudChart from './WordCloudChart';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Bell, Pencil } from 'lucide-react';

const DRILL_VIZ = new Set(['bar_chart', 'line_chart', 'area_chart']);

const ChartRendererInner = React.memo(function ChartRendererInner({ vizType, data, rowDims, colDim: _rawColDim, showGrid, showLegend, refLineValue, refLineLabel, vizConfig, formatValue, allFields, fieldOverrides, onDrill, highlightField, highlightValue, secondYAxis, themeColors, marks, discussions, onHover, drillPath, onDrillDown, onOtherToggle, onToggleStack, modelId, valueMeasures }) {
  // A field that is already on the axis (rowDims) must never also drive the
  // series/colour split. If it does — e.g. a date on both the x-axis and the
  // colour — we would emit one series per x value, an N×N cartesian that hangs
  // the chart (the reported "page unresponsive" freeze). Ignore it in that case.
  const colDim = (_rawColDim && (rowDims || []).includes(_rawColDim)) ? '' : (_rawColDim || '');
  // Legend-click series visibility toggle (declared before any early return).
  const [hiddenSeries, setHiddenSeries] = React.useState(() => new Set());
  // Measure the chart's own container so it fills whatever height the visual
  // is resized to (charts used to be a fixed 340px, which overflowed short
  // cards — cutting axis labels — and wasted space in tall ones).
  // Use a callback ref (not a mount-only effect): the component early-returns
  // null until data loads, so a `[]`-effect would attach the observer before
  // the node ever mounts and never re-run — leaving the chart stuck on its
  // fallback height and clipping labels/legend.
  const _wrapRef = React.useRef(null);
  const _roRef = React.useRef(null);
  const [_measuredH, _setMeasuredH] = React.useState(0);
  // Data-point right-click menu → Create alert (with the clicked dimension as a
  // condition) and Edit the calculated measure in the model.
  const _navigate = useNavigate();
  const { hasPermission } = useAuth();
  const _canEditModel = hasPermission && hasPermission('semantic_models:write');
  const _activeRef = React.useRef(null);
  const [dpMenu, setDpMenu] = React.useState(null);
  const [alertForm, setAlertForm] = React.useState(null);
  const _setWrapEl = React.useCallback((el) => {
    if (_roRef.current) { _roRef.current.disconnect(); _roRef.current = null; }
    _wrapRef.current = el;
    if (el && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver((entries) => {
        const h = entries[0]?.contentRect?.height || 0;
        if (h > 0) _setMeasuredH(Math.round(h));
      });
      ro.observe(el);
      _roRef.current = ro;
    }
  }, []);
  if (!data?.length) return null;

  // Build sort_by_field lookup from semantic model metadata + dynamic _sort_ columns
  const MONTH_ORDER = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
    january: 1, february: 2, march: 3, april: 4, june: 6, july: 7, august: 8, september: 9, october: 10, november: 11, december: 12 };
  const DAY_ORDER = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
    sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  const sortByMap = {};
  allFields.forEach(f => { if (f.sort_by_field) sortByMap[f.name] = f.sort_by_field; });
  // Check for _sort_ prefixed columns from date part extraction
  if (data[0]) {
    for (const key of Object.keys(data[0])) {
      if (key.startsWith('_sort_')) {
        const fieldName = key.substring(6);
        if (!sortByMap[fieldName]) sortByMap[fieldName] = key;
      }
    }
  }

  const getChartSortValue = (row, dim) => {
    const sortKey = sortByMap[dim];
    if (sortKey && row[sortKey] !== undefined) return row[sortKey];
    if (row['_sort_' + dim] !== undefined) return row['_sort_' + dim];
    const val = row[dim];
    if (val == null) return null;
    const lower = String(val).toLowerCase();
    if (MONTH_ORDER[lower] !== undefined) return MONTH_ORDER[lower];
    if (DAY_ORDER[lower] !== undefined) return DAY_ORDER[lower];
    return val;
  };

  // When a Color/Series dimension is set, the query returns LONG-format rows
  // (x, series, measure). Recharts needs WIDE format (one column per series) to
  // draw a series per value — otherwise only the measure renders as a single
  // series and the Color/Series pill appears to do nothing. Pivot here so every
  // consumer (dashboards, report builder, insights) gets multi-series output.
  const _seriesChart = ['bar_chart', 'line_chart', 'area_chart'].includes(vizType);
  const _measureKey = colDim && data?.length
    ? Object.keys(data[0]).find(k => !k.startsWith('_') && !rowDims.includes(k) && k !== colDim)
    : null;
  const _doPivot = _seriesChart && colDim && rowDims.length >= 1 && _measureKey
    && data.length && Object.prototype.hasOwnProperty.call(data[0], colDim);
  const effData = (() => {
    if (!_doPivot) return data;
    const xk = rowDims[0];
    const map = new Map();
    const seriesSet = new Set();
    for (const r of data) {
      const key = String(r[xk] ?? '');
      if (!map.has(key)) { const base = {}; base[xk] = r[xk]; map.set(key, base); }
      const sv = String(r[colDim] ?? '—');
      seriesSet.add(sv);
      const rec = map.get(key);
      rec[sv] = (rec[sv] || 0) + (Number(r[_measureKey]) || 0);
    }
    const series = Array.from(seriesSet);
    return Array.from(map.values()).map(rec => { series.forEach(s => { if (rec[s] === undefined) rec[s] = 0; }); return rec; });
  })();
  const effColDim = _doPivot ? '' : colDim;

  // Sort by all category dimensions in order, respecting sort_by_field
  const sortedData = [...effData].sort((a, b) => {
    for (const dim of rowDims) {
      const aVal = getChartSortValue(a, dim);
      const bVal = getChartSortValue(b, dim);
      if (aVal === bVal) continue;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') return aVal - bVal;
      return String(aVal).localeCompare(String(bVal));
    }
    return 0;
  });

  const keys = Object.keys(sortedData[0]).filter(k => !k.startsWith('_sort_') && !k.startsWith('_trend') && !k.startsWith('_movAvg') && !k.startsWith('_avg') && !k.startsWith('_median') && !k.startsWith('_std') && !k.startsWith('_xComposite') && !k.startsWith('_xInner') && !k.startsWith('_xOuter') && !k.startsWith('_forecast') && !k.startsWith('_isForecast') && !k.startsWith('_fc'));
  let xKey = rowDims[0] || keys[0];
  let vKeys = keys.filter(k => !rowDims.includes(k) && k !== effColDim);
  // Guard: a non-pivoted chart must only plot its configured value measures as
  // series — never stray columns that can leak into the row data (e.g. a raw-row
  // Mongo fallback). Match by exact key or the part after an "entity::" prefix;
  // fall back to all keys if nothing matches so we never render an empty chart.
  if (!effColDim && Array.isArray(valueMeasures) && valueMeasures.length) {
    const tail = (s) => (String(s).includes('::') ? String(s).split('::').pop() : String(s));
    const want = new Set(valueMeasures.map(tail));
    const restricted = vKeys.filter(k => want.has(k) || want.has(tail(k)));
    if (restricted.length) vKeys = restricted;
  }

  // Hierarchical X-axis: when 2+ dims, create composite labels
  const hasHierarchicalX = rowDims.length >= 2;
  const cfgEarly = vizConfig || {};
  // Interactive drill-down on hierarchical axes: start aggregated at the outer dim,
  // click a bar/point to expand into the next dimension level.
  const path = drillPath || [];
  const drilldownActive = !!cfgEarly.drilldownHierarchy && hasHierarchicalX && DRILL_VIZ.has(vizType);
  const useHier = hasHierarchicalX && !drilldownActive;

  let chartData = sortedData;
  let innerDim = xKey;
  let outerDim = null;
  let effectiveXKey;

  if (drilldownActive) {
    const level = Math.min(path.length, rowDims.length - 1);
    const drillDim = rowDims[level];
    const filtered = sortedData.filter(row => path.every((val, i) => String(row[rowDims[i]] ?? '') === String(val)));
    const groups = {};
    filtered.forEach(row => {
      const k = String(row[drillDim] ?? '');
      if (!groups[k]) {
        groups[k] = { [drillDim]: row[drillDim] };
        if (row['_sort_' + drillDim] !== undefined) groups[k]['_sort_' + drillDim] = row['_sort_' + drillDim];
        vKeys.forEach(m => { groups[k][m] = 0; });
      }
      vKeys.forEach(m => { if (typeof row[m] === 'number') groups[k][m] += row[m]; });
    });
    chartData = Object.values(groups).sort((a, b) => {
      const av = getChartSortValue(a, drillDim);
      const bv = getChartSortValue(b, drillDim);
      if (av === bv) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv));
    });
    xKey = drillDim;
    effectiveXKey = drillDim;
  } else if (hasHierarchicalX) {
    outerDim = rowDims[0];
    innerDim = rowDims[rowDims.length - 1];
    chartData = sortedData.map(row => ({
      ...row,
      _xComposite: rowDims.map(d => String(row[d] ?? '')).join(' | '),
      _xInner: String(row[innerDim] ?? ''),
      _xOuter: String(row[outerDim] ?? ''),
    }));
    effectiveXKey = '_xComposite';
  } else {
    effectiveXKey = xKey;
  }
  const canDrillDeeper = drilldownActive && path.length < rowDims.length - 1;

  // Custom hierarchical tick renderer
  const HierarchicalTick = ({ x, y, payload, index }) => {
    if (!useHier) return <text x={x} y={y + 10} textAnchor="middle" fill="#a1a1aa" fontSize={10}>{formatValue(payload.value, xKey)}</text>;
    const row = chartData[index];
    if (!row) return null;
    const inner = row._xInner;
    const outer = row._xOuter;
    // Check if this is the first item of a new outer group
    const prevRow = index > 0 ? chartData[index - 1] : null;
    const isNewGroup = !prevRow || prevRow._xOuter !== outer;
    // Count items in this group for centering the outer label
    let groupSize = 0;
    let groupStart = index;
    for (let i = index; i < chartData.length && chartData[i]._xOuter === outer; i++) groupSize++;
    for (let i = index - 1; i >= 0 && chartData[i]._xOuter === outer; i--) groupStart--;
    const isGroupCenter = index === Math.floor(groupStart + (groupSize / 2));
    return (
      <g>
        <text x={x} y={y + 10} textAnchor="middle" fill="#a1a1aa" fontSize={9}>{inner}</text>
        {isGroupCenter && <text x={x} y={y + 24} textAnchor="middle" fill="#e2e8f0" fontSize={10} fontWeight={600}>{outer}</text>}
        {isNewGroup && index > 0 && <line x1={x - 20} y1={y - 2} x2={x - 20} y2={y + 28} stroke="rgba(255,255,255,0.15)" strokeWidth={1} />}
      </g>
    );
  };
  const ref = refLineValue ? Number(refLineValue) : null;
  const drillOrFilter = (val) => {
    if (val === undefined || val === null) return;
    if (canDrillDeeper && onDrillDown) onDrillDown(val);
    else onDrill(val);
  };
  const click = (e) => { if (e?.activeLabel !== undefined) drillOrFilter(e.activeLabel); };
  const barClick = (d) => { if (d && d[xKey] !== undefined) drillOrFilter(d[xKey]); };
  const chartMouseMove = (e) => {
    if (e?.activePayload?.[0]) { _activeRef.current = { row: e.activePayload[0].payload, dataKey: e.activePayload[0].dataKey }; }
    if (onHover && e?.activePayload?.[0]?.payload) onHover(e.activePayload[0].payload);
  };
  const _labelOf = (name) => getFieldLabel(allFields.find(f => f.name === name), name, fieldOverrides?.[name]) || String(name);
  const handleDataPointContextMenu = (e) => {
    let row = _activeRef.current?.row;
    let dataKey = _activeRef.current?.dataKey;
    // Fallback: recharts' React mouse events don't always fire (some browsers /
    // synthetic events), so map the cursor to the bar under it via the DOM.
    if (!row) {
      try {
        const rects = Array.from((e.currentTarget || _wrapRef.current)?.querySelectorAll('.recharts-bar-rectangle') || []);
        let idx = rects.findIndex(r => { const b = r.getBoundingClientRect(); return e.clientX >= b.left && e.clientX <= b.right && e.clientY >= b.top && e.clientY <= b.bottom; });
        if (idx < 0 && rects.length) {
          // nearest by horizontal center
          let best = Infinity;
          rects.forEach((r, i) => { const b = r.getBoundingClientRect(); const d = Math.abs((b.left + b.right) / 2 - e.clientX); if (d < best) { best = d; idx = i; } });
        }
        if (idx >= 0 && data.length) row = data[idx % data.length];
      } catch (_) { /* ignore */ }
    }
    if (!row) return;
    e.preventDefault();
    e.stopPropagation();
    const dimField = xKey;
    const dimValue = row[xKey];
    const numericKeys = Object.keys(row).filter(k => typeof row[k] === 'number' && !k.startsWith('_'));
    const metric = (dataKey && typeof row[dataKey] === 'number') ? dataKey : ((vizConfig?.valueMeasures || [])[0] || numericKeys[0]);
    const isCalc = !!allFields.find(f => f.name === metric)?.is_calculated;
    setDpMenu({ x: e.clientX, y: e.clientY, dimField, dimValue, metric, isCalc, current: row[metric] });
  };
  const openAlertFromPoint = () => {
    if (!dpMenu) return;
    const cur = dpMenu.current;
    setAlertForm({
      name: `Alert: ${_labelOf(dpMenu.metric)} for ${dpMenu.dimValue}`,
      metric: dpMenu.metric, operator: 'gt',
      threshold: typeof cur === 'number' ? String(Math.round(cur * 1.1)) : '',
      frequency: 'daily', recipients: '',
      dimField: dpMenu.dimField, dimValue: dpMenu.dimValue,
      _metricLabel: _labelOf(dpMenu.metric), _dimLabel: _labelOf(dpMenu.dimField), _current: cur,
    });
    setDpMenu(null);
  };
  const editMeasureInModel = () => {
    if (!dpMenu) return;
    const mid = modelId || vizConfig?.model_id;
    const params = new URLSearchParams();
    if (mid) params.set('model', mid);
    params.set('measure', dpMenu.metric);
    _navigate(`/semantic-models?${params.toString()}`);
    setDpMenu(null);
  };
  const submitPointAlert = async () => {
    try {
      await api.createAlert({
        name: alertForm.name, metric: alertForm.metric, operator: alertForm.operator,
        threshold: Number(alertForm.threshold), check_frequency: alertForm.frequency,
        recipients: alertForm.recipients.split(',').map(s => s.trim()).filter(Boolean), severity: 'medium',
        dimension_filters: [{ field: alertForm.dimField, operator: 'eq', value: alertForm.dimValue }],
      });
      toast.success('Alert created');
      setAlertForm(null);
    } catch (err) { toast.error(err.message || 'Could not create alert'); }
  };
  const fmtTick = (val) => formatValue(val, xKey);
  const fmtYTick = (val) => {
    if (typeof val !== 'number') return String(val);
    if (Math.abs(val) >= 1000000) return (val / 1000000).toFixed(1) + 'M';
    if (Math.abs(val) >= 1000) return (val / 1000).toFixed(0) + 'K';
    return val.toLocaleString();
  };
  const cfg = vizConfig || {};
  const colors = themeColors || PALETTES[cfg.palette || 'default'] || PALETTES.default;
  // Per-series color lock: a user-assigned color for a specific series value
  // wins over the palette. The rolled-up "Other" bucket gets a neutral gray.
  const seriesColorMap = cfg.seriesColors || {};
  const seriesColor = (k, i) => {
    if (seriesColorMap[k]) return seriesColorMap[k];
    if (k === 'Other') return '#9ca3af';
    return colors[i % colors.length];
  };
  // Clicking a legend item toggles that series' visibility (hide/show). The
  // rolled-up "Other" bucket keeps its drill behaviour.
  const handleLegendClick = (entry) => {
    const key = entry?.dataKey ?? entry?.value ?? entry?.payload?.dataKey ?? entry?.payload?.value;
    const k = String(key);
    if (k === 'Other' && onOtherToggle) { onOtherToggle(); return; }
    if (!k || k === 'undefined') return;
    setHiddenSeries(prev => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };
  // Grey a hidden series' legend label so it's clear what's toggled off.
  const legendFormatter = (v, entry) => <span style={{ opacity: hiddenSeries.has(String(entry?.dataKey ?? v)) ? 0.35 : 1 }}>{fmtLegend(v)}</span>;
  const legendProps = { wrapperStyle: { fontSize: 10, cursor: 'pointer' }, formatter: legendFormatter, onClick: handleLegendClick };
  // Pie slices are keyed by slice name (nameKey), not a dataKey — grey the
  // hidden slice's legend label to match the Cartesian toggle behaviour.
  const pieLegendFormatter = (v) => <span style={{ opacity: hiddenSeries.has(String(v)) ? 0.35 : 1 }}>{fmtLegend(v)}</span>;
  const curveType = cfg.curveType || 'monotone';
  const lineW = cfg.lineWidth || 2;
  const barR = cfg.barRadius || 4;
  const dashArray = cfg.lineStyle === 'dashed' ? '8 4' : cfg.lineStyle === 'dotted' ? '2 2' : undefined;

  const getFieldDisplayName = (fieldName) => {
    const ovr = fieldOverrides[fieldName];
    const f = allFields.find(ff => ff.name === fieldName);
    return getFieldLabel(f, fieldName, ovr);
  };
  // Succinct label for legends/axes: strips a redundant "Table · Field" /
  // "Table - Field" prefix so the legend shows just the measure name (e.g.
  // "Claim Count" instead of "Claim Measures - Claim Count"). The fully
  // qualified name still appears in the tooltip via getFieldDisplayName.
  const getFieldShortName = (fieldName) => {
    const f = allFields.find(ff => ff.name === fieldName);
    if (f && f._baseLabel) return f._baseLabel;
    const full = getFieldDisplayName(fieldName);
    if (typeof full !== 'string') return full;
    // Strip a leading entity/table qualifier followed by ·, -, – or — .
    const m = full.match(/^(.*?)\s*[·\u2013\u2014-]\s+(.+)$/);
    if (m) {
      const prefix = m[1].trim().toLowerCase();
      const norm = (s) => String(s || '').replace(/_/g, ' ').trim().toLowerCase();
      const entityMatch = f && (norm(f.entity_name) === prefix || norm(f.entity_logical_name) === prefix);
      // "·" is the app's reserved qualifier, so always strip it; for a hyphen,
      // only strip when the prefix matches this field's entity/table name.
      if (full.includes('\u00b7') || entityMatch) return m[2].trim();
    }
    return full;
  };
  const fmtLegend = (value) => getFieldShortName(value);
  const fmtTooltip = (val, name) => [formatValue(val, name), getFieldDisplayName(name)];

  // Axis titles (measure/dimension name + unit suffix), toggleable per chart
  // via `showAxisTitles` (default on) so authors can strip them for a minimal look.
  const showAxisTitles = cfg.showAxisTitles !== false;
  const axisTitleFor = (fieldName) => {
    if (!fieldName) return '';
    const f = allFields.find(ff => ff.name === fieldName);
    const suffix = unitAxisSuffix(f?.unit);
    return getFieldDisplayName(fieldName) + (suffix ? ` ${suffix}` : '');
  };
  const axisLabelStyle = { fontSize: 10, fill: '#a1a1aa', textAnchor: 'middle' };
  const yTitleLabel = showAxisTitles ? { value: axisTitleFor(vKeys[0]), angle: -90, position: 'insideLeft', style: axisLabelStyle } : undefined;

  // Custom tooltip that shows ALL dimension fields + discussion indicator
  const discList = discussions || [];
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload || {};
    // Check if any discussion matches this data point
    const matchingDiscs = discList.filter(d => {
      const ctx = d.data_context || {};
      return Object.entries(ctx).some(([k, v]) => {
        if (k === '_row_count') return false;
        const rowVal = String(row[k] ?? '');
        const ctxVals = String(v).split(', ');
        return ctxVals.includes(rowVal);
      });
    });
    return (
      <div style={TT_STYLE}>
        {rowDims.map(dim => (
          <p key={dim} style={{ ...TT_LABEL_STYLE, marginBottom: 3 }}>
            <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10 }}>{getFieldDisplayName(dim)}: </span>
            {formatValue(row[dim], dim)}
          </p>
        ))}
        {mk.color && row[mk.color] !== undefined && !rowDims.includes(mk.color) && (
          <p style={{ ...TT_LABEL_STYLE, marginBottom: 3 }}>
            <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10 }}>{getFieldDisplayName(mk.color)}: </span>
            {formatValue(row[mk.color], mk.color)}
          </p>
        )}
        {mk.detail && row[mk.detail] !== undefined && !rowDims.includes(mk.detail) && (
          <p style={{ ...TT_LABEL_STYLE, marginBottom: 3 }}>
            <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10 }}>{getFieldDisplayName(mk.detail)}: </span>
            {formatValue(row[mk.detail], mk.detail)}
          </p>
        )}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', margin: '6px 0', paddingTop: 6 }}>
          {payload.map((p, i) => {
            const priorPct = row[p.dataKey + '__prior_pct'];
            const priorVal = row[p.dataKey + '__prior'];
            return (
              <div key={i} style={{ marginBottom: 3 }}>
                <p style={{ ...TT_ITEM_STYLE, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, display: 'inline-block', flexShrink: 0 }} />
                  <span>{getFieldDisplayName(p.dataKey)}: </span>
                  <span style={{ fontWeight: 600 }}>{formatValue(p.value, p.dataKey)}</span>
                </p>
                {typeof priorPct === 'number' && (
                  <p style={{ ...TT_ITEM_STYLE, marginLeft: 16, fontSize: 10, color: priorPct >= 0 ? '#10b981' : '#ef4444' }}>
                    {priorPct >= 0 ? '\u25B2' : '\u25BC'} {Math.abs(priorPct)}% vs same point last year ({formatValue(priorVal, p.dataKey)})
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {matchingDiscs.length > 0 && (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: 6, paddingTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            <span style={{ color: '#f59e0b', fontSize: 10, fontWeight: 600 }}>{matchingDiscs.length} discussion{matchingDiscs.length > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
    );
  };

  // Highlight: compute opacity per data point
  const getBarOpacity = (entry, dataKey) => {
    if (!highlightField || !highlightValue) return 1;
    return entry[highlightField] === highlightValue ? 1 : 0.2;
  };

  // ─── Marks processing ───
  const mk = marks || {};
  // Color mark: build a color map from unique values of the color dimension
  let markColorMap = null;
  if (mk.color && chartData[0]?.[mk.color] !== undefined) {
    const uniqueVals = [...new Set(chartData.map(r => r[mk.color]).filter(Boolean))];
    markColorMap = {};
    uniqueVals.forEach((v, i) => { markColorMap[String(v)] = colors[i % colors.length]; });
  }
  // Label mark: render labels on bars/lines
  const markLabelField = mk.label || null;
  const renderMarkLabel = markLabelField ? (props) => {
    const { x, y, width, value, index, payload } = props;
    const row = payload || chartData[index];
    if (!row) return null;
    const lv = row[markLabelField];
    if (lv == null) return null;
    const xPos = width ? x + width / 2 : x;
    return <text x={xPos} y={y - 6} textAnchor="middle" fill="#e2e8f0" fontSize={9} fontWeight={500}>{formatValue(lv, markLabelField)}</text>;
  } : false;

  // Formatted data-value labels (customize view → "Labels"). Uses unit-aware
  // formatValue; on crowded charts labels auto-thin to ~24 so they stay readable.
  const dataLabelCfg = (k, position) => {
    if (!cfg.dataLabels) return false;
    const step = Math.max(1, Math.ceil((chartData?.length || 0) / 24));
    if (step === 1) {
      return { position: position || 'top', fontSize: 9, fill: '#94a3b8', formatter: (v) => formatValue(v, k) };
    }
    return (props) => {
      const { x, y, value, index, width } = props || {};
      if (value == null || value === '' || (index % step !== 0)) return null;
      const cx = (typeof width === 'number') ? x + width / 2 : x;
      const cy = position === 'right' ? y : y - 5;
      return React.createElement('text', { x: cx, y: cy, textAnchor: 'middle', fontSize: 9, fill: '#94a3b8', fontWeight: 500 }, formatValue(value, k));
    };
  };

  const chartHeight = _measuredH > 40 ? _measuredH : (useHier ? 380 : 340);
  const xAxisProps = useHier ? { dataKey: effectiveXKey, tick: <HierarchicalTick />, interval: 0, height: 50, axisLine: false, tickLine: false } : { dataKey: xKey, ...AXIS_PROPS, tickFormatter: fmtTick };
  const yAxisProps = { ...AXIS_PROPS, tickFormatter: fmtYTick };
  // Natural-language axis controls: log scale and zero-baseline.
  if (cfg.logScale) { yAxisProps.scale = 'log'; yAxisProps.domain = ['auto', 'auto']; yAxisProps.allowDataOverflow = true; }
  else if (cfg.axisFromZero) { yAxisProps.domain = [0, 'auto']; yAxisProps.allowDataOverflow = false; }

  // ─── Analytics overlays (trend line, moving avg, stats lines) ───
  const analyticsElements = [];
  if (cfg && vKeys.length > 0 && chartData.length >= 2) {
    const measureKey = vKeys[0];
    const vals = chartData.map(d => typeof d[measureKey] === 'number' ? d[measureKey] : 0);
    const n = vals.length;

    // Average line
    if (cfg.avgLine) {
      const avg = vals.reduce((s, v) => s + v, 0) / n;
      analyticsElements.push(<ReferenceLine key="avg" y={avg} stroke="#10b981" strokeDasharray="8 4" strokeWidth={1.5} label={{ value: `Avg: ${fmtYTick(avg)}`, position: 'right', fontSize: 9, fill: '#10b981' }} />);
      // Add to chartData for legend entry
      chartData = chartData.map(d => ({ ...d, _avg: avg }));
    }

    // Median line
    if (cfg.medianLine) {
      const sorted = [...vals].sort((a, b) => a - b);
      const median = n % 2 === 0 ? (sorted[n/2 - 1] + sorted[n/2]) / 2 : sorted[Math.floor(n/2)];
      analyticsElements.push(<ReferenceLine key="median" y={median} stroke="#f59e0b" strokeDasharray="6 3" strokeWidth={1.5} label={{ value: `Med: ${fmtYTick(median)}`, position: 'right', fontSize: 9, fill: '#f59e0b' }} />);
      chartData = chartData.map(d => ({ ...d, _median: median }));
    }

    // Standard deviation bands
    if (cfg.stdDevBands) {
      const avg = vals.reduce((s, v) => s + v, 0) / n;
      const variance = vals.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / n;
      const stdDev = Math.sqrt(variance);
      analyticsElements.push(<ReferenceArea key="stddev" y1={avg - stdDev} y2={avg + stdDev} fill="#8b5cf6" fillOpacity={0.08} stroke="#8b5cf6" strokeOpacity={0.2} strokeDasharray="4 4" />);
      analyticsElements.push(<ReferenceLine key="stddev-upper" y={avg + stdDev} stroke="#8b5cf6" strokeDasharray="3 3" strokeWidth={0.5} />);
      analyticsElements.push(<ReferenceLine key="stddev-lower" y={avg - stdDev} stroke="#8b5cf6" strokeDasharray="3 3" strokeWidth={0.5} />);
      chartData = chartData.map(d => ({ ...d, _stdUpper: avg + stdDev }));
    }

    // Trend line (rendered as a Line in ComposedChart — will add data points)
    if (cfg.trendLine) {
      const trendType = cfg.trendType || 'linear';
      if (trendType === 'linear') {
        const xMean = (n - 1) / 2;
        const yMean = vals.reduce((s, v) => s + v, 0) / n;
        let num = 0, den = 0;
        vals.forEach((y, i) => { num += (i - xMean) * (y - yMean); den += (i - xMean) * (i - xMean); });
        const slope = den !== 0 ? num / den : 0;
        const intercept = yMean - slope * xMean;
        chartData = chartData.map((d, i) => ({ ...d, _trend: intercept + slope * i }));
      }
    }

    // Moving average
    if (cfg.movingAvg) {
      const w = cfg.movingAvgWindow || 3;
      chartData = chartData.map((d, i) => {
        const start = Math.max(0, i - w + 1);
        const windowSlice = vals.slice(start, i + 1);
        return { ...d, _movAvg: windowSlice.reduce((s, v) => s + v, 0) / windowSlice.length };
      });
    }

    // Forecasting
    if (cfg.forecast) {
      const periods = cfg.forecastPeriods || 3;
      const method = cfg.forecastMethod || 'linear';
      const showCI = cfg.forecastCI !== false;

      const forecastVals = computeForecastValues(vals, method, periods, cfg.forecastSeasonLength || 12);

      // Compute confidence interval
      const residuals = vals.map((v) => v - (vals.reduce((s, vv) => s + vv, 0) / n));
      const stdErr = Math.sqrt(residuals.reduce((s, r) => s + r * r, 0) / Math.max(n - 2, 1));

      // Add forecast data points to chartData — use the actual measure key so bars/lines render
      chartData = chartData.map((d, i) => ({
        ...d,
        _isForecast: false,
        _forecast: undefined,
        _forecastUpper: undefined,
        _forecastLower: undefined,
      }));

      for (let i = 0; i < periods; i++) {
        const fcPoint = {
          [xKey]: `F+${i + 1}`,
          [effectiveXKey]: `F+${i + 1}`,
          [measureKey]: forecastVals[i],
          _isForecast: true,
          _forecast: forecastVals[i],
        };
        if (showCI) {
          const ciWidth = stdErr * 1.96 * Math.sqrt(1 + (i + 1) / n);
          fcPoint._forecastUpper = forecastVals[i] + ciWidth;
          fcPoint._forecastLower = forecastVals[i] - ciWidth;
          fcPoint._forecastBandWidth = ciWidth * 2;
        }
        chartData = [...chartData, fcPoint];
      }
    }
  }

  const hasAnalyticsLines = cfg && (cfg.trendLine || cfg.movingAvg || cfg.avgLine || cfg.medianLine || cfg.stdDevBands || cfg.forecast || (ref && refLineLabel));
  const analyticsLines = [];
  if (cfg.avgLine && chartData[0]?._avg !== undefined) {
    analyticsLines.push(<Line key="_avg" type="monotone" dataKey="_avg" stroke="#10b981" strokeWidth={1.5} strokeDasharray="8 4" dot={false} name="Average" legendType="plainline" />);
  }
  if (cfg.medianLine && chartData[0]?._median !== undefined) {
    analyticsLines.push(<Line key="_median" type="monotone" dataKey="_median" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="6 3" dot={false} name="Median" legendType="plainline" />);
  }
  if (cfg.stdDevBands && chartData[0]?._stdUpper !== undefined) {
    analyticsLines.push(<Line key="_stdUpper" type="monotone" dataKey="_stdUpper" stroke="#8b5cf6" strokeWidth={0.5} strokeDasharray="3 3" dot={false} name="±1σ Std Dev" legendType="plainline" />);
  }
  if (hasAnalyticsLines) {
    if (cfg.trendLine && chartData[0]?._trend !== undefined) {
      analyticsLines.push(<Line key="_trend" type="monotone" dataKey="_trend" stroke="#ef4444" strokeWidth={2} strokeDasharray="8 4" dot={false} name="Trend" legendType="plainline" />);
    }
    if (cfg.movingAvg && chartData[0]?._movAvg !== undefined) {
      analyticsLines.push(<Line key="_movAvg" type="monotone" dataKey="_movAvg" stroke="#06b6d4" strokeWidth={2} strokeDasharray="6 3" dot={false} name={`${cfg.movingAvgWindow || 3}-period MA`} legendType="plainline" />);
    }
  }
  // Forecast CI band — shade between upper and lower bounds only
  if (cfg.forecast) {
    if (cfg.forecastCI !== false) {
      // Render lower bound as solid background-color area (masks below), upper as orange fill
      analyticsLines.push(<Area key="_fcLower" type="monotone" dataKey="_forecastLower" stackId="fcBand" stroke="none" fill="var(--background, #09090b)" fillOpacity={1} dot={false} legendType="none" connectNulls />);
      analyticsLines.push(<Area key="_fcBand" type="monotone" dataKey="_forecastBandWidth" stackId="fcBand" stroke="#f97316" strokeWidth={0.5} strokeDasharray="3 3" fill="#f97316" fillOpacity={0.15} dot={false} name="95% CI" legendType="rect" connectNulls />);
    }
  }

  // Reference line legend entry
  if (ref && refLineLabel && chartData.length > 0) {
    chartData = chartData.map(d => ({ ...d, _refLine: ref }));
    analyticsLines.push(<Line key="_refLine" type="monotone" dataKey="_refLine" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="5 5" dot={false} name={refLineLabel} legendType="plainline" />);
  }

  // ─── Dual Y-Axis via ComposedChart ───
  const s2 = secondYAxis || {};
  if (s2.field && s2.chartType && vKeys.includes(s2.field) && (vizType === 'bar_chart' || vizType === 'line_chart' || vizType === 'area_chart')) {
    const primaryKeys = vKeys.filter(k => k !== s2.field);
    const renderPrimary = (k, i) => {
      if (vizType === 'bar_chart') return <Bar key={k} dataKey={k} hide={hiddenSeries.has(k)} yAxisId="left" fill={seriesColor(k, i)} radius={[barR, barR, 0, 0]} cursor="pointer" onClick={barClick} onMouseEnter={(d) => { _activeRef.current = { row: (d && d.payload) || d, dataKey: k }; }} />;
      if (vizType === 'line_chart') return <Line key={k} type={curveType} dataKey={k} hide={hiddenSeries.has(k)} yAxisId="left" stroke={seriesColor(k, i)} strokeWidth={lineW} activeDot={{ onMouseEnter: (_e, p) => { if (p && p.payload) _activeRef.current = { row: p.payload, dataKey: k }; } }} dot={cfg.showDots !== false ? { r: 3 } : false} />;
      return <Area key={k} type={curveType} dataKey={k} hide={hiddenSeries.has(k)} yAxisId="left" stroke={seriesColor(k, i)} fill={seriesColor(k, i)} fillOpacity={cfg.fillOpacity || 0.2} />;
    };
    const si = vKeys.indexOf(s2.field);
    const secColor = colors[(si >= 0 ? si : primaryKeys.length) % colors.length];
    // Axis titles so a dual-axis combo reads at a glance: left = primary
    // measure(s), right = the second-axis measure. Toggleable via showAxisTitles.
    const leftTitle = axisTitleFor(primaryKeys[0] || vKeys[0]);
    const rightTitle = axisTitleFor(s2.field);
    const renderSecondary = () => {
      if (s2.chartType === 'bar') return <Bar dataKey={s2.field} yAxisId="right" fill={secColor} radius={[barR, barR, 0, 0]} cursor="pointer" onClick={barClick} fillOpacity={0.7} />;
      if (s2.chartType === 'line') return <Line type={curveType} dataKey={s2.field} yAxisId="right" stroke={secColor} strokeWidth={lineW + 1} dot={{ r: 4 }} strokeDasharray="6 3" />;
      if (s2.chartType === 'area') return <Area type={curveType} dataKey={s2.field} yAxisId="right" stroke={secColor} fill={secColor} fillOpacity={0.15} />;
      return <Line type={curveType} dataKey={s2.field} yAxisId="right" stroke={secColor} strokeWidth={lineW} />;
    };
    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart data={chartData} onClick={click}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />}
          <XAxis {...xAxisProps} />
          <YAxis yAxisId="left" {...yAxisProps} label={showAxisTitles ? { value: leftTitle, angle: -90, position: 'insideLeft', style: axisLabelStyle } : undefined} />
          <YAxis yAxisId="right" orientation="right" {...yAxisProps} tick={{ fontSize: 10, fill: secColor }} label={showAxisTitles ? { value: rightTitle, angle: 90, position: 'insideRight', style: { ...axisLabelStyle, fill: secColor } } : undefined} />
          <ReTooltip content={<CustomTooltip />} />
          {showLegend && <Legend {...legendProps} />}
          {ref && <ReferenceLine yAxisId="left" y={ref} stroke="#ef4444" strokeDasharray="5 5" />}
          {primaryKeys.map((k, i) => renderPrimary(k, i))}
          {renderSecondary()}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  const _viz = (() => {
  switch (vizType) {
    case 'bar_chart': return (<ResponsiveContainer width="100%" height={chartHeight}><ComposedChart data={chartData} layout={hasAnalyticsLines ? 'horizontal' : (cfg.orientation === 'horizontal' ? 'vertical' : 'horizontal')} onClick={click} onMouseMove={chartMouseMove}>{showGrid && <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />}{cfg.orientation === 'horizontal' ? <React.Fragment><YAxis dataKey={effectiveXKey} type="category" {...AXIS_PROPS} tickFormatter={fmtTick} width={100} /><XAxis type="number" {...AXIS_PROPS} label={showAxisTitles ? { value: axisTitleFor(vKeys[0]), position: 'insideBottom', offset: -2, style: axisLabelStyle } : undefined} /></React.Fragment> : <React.Fragment><XAxis {...xAxisProps} /><YAxis {...yAxisProps} label={yTitleLabel} /></React.Fragment>}<ReTooltip content={<CustomTooltip />} />{showLegend && <Legend {...legendProps} />}{analyticsElements}{ref && <ReferenceLine y={ref} stroke="#ef4444" strokeDasharray="5 5" label={{ value: refLineLabel || String(ref), fontSize: 10, fill: '#ef4444' }} />}{vKeys.map((k, i) => <Bar key={k} dataKey={k} hide={hiddenSeries.has(k)} fill={seriesColor(k, i)} radius={[barR, barR, 0, 0]} cursor="pointer" onClick={barClick} stackId={cfg.barLayout === 'stacked' || cfg.barLayout === 'percent' ? 'stack' : undefined} label={renderMarkLabel || dataLabelCfg(k, cfg.orientation === 'horizontal' ? 'right' : 'top')}>{chartData.map((entry, idx) => <Cell key={idx} fill={markColorMap ? (markColorMap[String(entry[mk.color])] || seriesColor(k, i)) : seriesColor(k, i)} stroke={entry._isForecast ? "#f97316" : "none"} strokeWidth={entry._isForecast ? 1.5 : 0} strokeDasharray={entry._isForecast ? "4 2" : "none"} fillOpacity={entry._isForecast ? 0.4 : (highlightField && highlightValue ? getBarOpacity(entry, k) : 1)} />)}</Bar>)}{analyticsLines.length > 0 && analyticsLines}</ComposedChart></ResponsiveContainer>);
    case 'line_chart': return (<ResponsiveContainer width="100%" height={chartHeight}><ComposedChart data={chartData} onClick={click} onMouseMove={chartMouseMove}>{showGrid && <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />}<XAxis {...xAxisProps} /><YAxis {...yAxisProps} label={yTitleLabel} /><ReTooltip content={<CustomTooltip />} />{showLegend && <Legend {...legendProps} />}{analyticsElements}{ref && <ReferenceLine y={ref} stroke="#ef4444" strokeDasharray="5 5" />}{vKeys.map((k, i) => <Line key={k} type={curveType} dataKey={k} hide={hiddenSeries.has(k)} stroke={seriesColor(k, i)} strokeWidth={lineW} strokeDasharray={dashArray} dot={cfg.showDots !== false ? { r: mk.size ? undefined : 3 } : false} label={renderMarkLabel || dataLabelCfg(k, 'top')} activeDot={{ r: 5, cursor: 'pointer', onClick: (_, payload) => { if (payload?.payload?.[xKey] !== undefined) onDrill(payload.payload[xKey]); } }} />)}{analyticsElements}{analyticsLines}</ComposedChart></ResponsiveContainer>);
    case 'area_chart': return (<ResponsiveContainer width="100%" height={chartHeight}><ComposedChart data={chartData} onClick={click} onMouseMove={chartMouseMove}>{showGrid && <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />}<XAxis {...xAxisProps} /><YAxis {...yAxisProps} label={yTitleLabel} /><ReTooltip content={<CustomTooltip />} />{showLegend && <Legend {...legendProps} />}{vKeys.map((k, i) => <Area key={k} type={curveType} dataKey={k} hide={hiddenSeries.has(k)} stroke={seriesColor(k, i)} fill={seriesColor(k, i)} fillOpacity={cfg.fillOpacity || 0.2} strokeWidth={lineW} stackId={cfg.stacked ? 'stack' : undefined} label={renderMarkLabel || dataLabelCfg(k, 'top')} activeDot={{ r: 5, cursor: 'pointer', onClick: (_, payload) => { if (payload?.payload?.[xKey] !== undefined) onDrill(payload.payload[xKey]); } }} />)}{analyticsElements}{analyticsLines}</ComposedChart></ResponsiveContainer>);
    case 'pie_chart': return (<ResponsiveContainer width="100%" height={chartHeight}><PieChart><Pie data={sortedData} cx="50%" cy="50%" innerRadius={cfg.donut !== false ? (cfg.innerRadius || 50) : 0} outerRadius={cfg.outerRadius || 100} dataKey={vKeys[0] || 'value'} nameKey={xKey} paddingAngle={3} strokeWidth={0} cursor="pointer" onMouseEnter={(_, i) => onHover && onHover(sortedData[i])} onClick={(entry) => { if (entry?.[xKey] !== undefined) onDrill(entry[xKey]); }} label={renderMarkLabel || ((cfg.dataLabels || cfg.showLabels) ? ({ value }) => formatValue(value, vKeys[0] || 'value') : false)}>{sortedData.map((entry, i) => <Cell key={i} fill={markColorMap ? (markColorMap[String(entry[mk.color])] || colors[i % colors.length]) : colors[i % colors.length]} fillOpacity={hiddenSeries.has(String(entry[xKey])) ? 0.15 : (highlightField && highlightValue ? (entry[highlightField] === highlightValue ? 1 : 0.2) : 1)} />)}</Pie><ReTooltip content={<CustomTooltip />} />{showLegend && <Legend wrapperStyle={{ fontSize: 10, cursor: 'pointer' }} formatter={pieLegendFormatter} onClick={handleLegendClick} />}</PieChart></ResponsiveContainer>);
    case 'scatter': return <ScatterPlot data={sortedData} xField={vKeys[0] || xKey} yField={vKeys[1] || vKeys[0]} sizeField={vKeys[2]} height={340} formatValue={formatValue} config={cfg} />;
    case 'bubble':  return <ScatterPlot data={sortedData} xField={vKeys[0] || xKey} yField={vKeys[1] || vKeys[0]} sizeField={vKeys[2] || vKeys[0]} height={340} formatValue={formatValue} config={cfg} />;
    case 'combo': return <ComboChart data={sortedData} xKey={xKey} barKeys={vKeys.slice(0, 1)} lineKeys={vKeys.slice(1)} height={340} showGrid={showGrid} formatValue={formatValue} config={{ ...cfg, leftLabel: showAxisTitles ? axisTitleFor(vKeys[0]) : '', rightLabel: showAxisTitles && vKeys[1] ? axisTitleFor(vKeys[1]) : '' }} />;
    case 'gauge': return <GaugeChart value={sortedData[0] ? sortedData[0][vKeys[0]] : 0} max={cfg.maxValue || Math.max(...sortedData.map(d => d[vKeys[0]] || 0)) * 1.2} label={vKeys[0]} height={250} />;
    case 'funnel': return <FunnelChartViz data={sortedData} nameKey={xKey} valueKey={vKeys[0] || 'value'} height={340} />;
    case 'waterfall': return <WaterfallChart data={sortedData} xKey={xKey} valueKey={vKeys[0]} height={340} />;
    case 'treemap': return <TreemapChart data={sortedData} nameKey={xKey} valueKey={vKeys[0] || 'value'} height={340} formatValue={formatValue} />;
    case 'wordcloud': {
      // The color measure is a distinct value column; size uses the other one.
      // Prefer an exact name match, else fall back positionally (the color
      // measure is appended after the size measure) so a data-key vs field-name
      // mismatch never silently disables the gradient.
      const cmName = cfg.colorMeasure;
      let sizeKey = vKeys[0];
      let colorKey = null;
      if (cmName && vKeys.length >= 2) {
        colorKey = vKeys.includes(cmName) ? cmName : vKeys[vKeys.length - 1];
        sizeKey = vKeys.find(k => k !== colorKey) || vKeys[0];
      }
      return <WordCloudChart data={sortedData} nameKey={xKey} valueKey={sizeKey} colorKey={colorKey} colorFrom={cfg.colorFrom} colorTo={cfg.colorTo} rotate={cfg.wcRotate} colors={colors} height={chartHeight} formatValue={formatValue} />;
    }
    case 'sankey': return <SankeyChart data={sortedData} sourceKey={rowDims[0] || xKey} targetKey={rowDims[1] || colDim} valueKey={vKeys[0] || 'value'} height={360} formatValue={formatValue} />;
    case 'packed_bubbles': return <PackedBubbleChart data={sortedData} nameKey={xKey} valueKey={vKeys[0] || 'value'} height={360} formatValue={formatValue} />;
    case 'heatmap': return rowDims.length >= 2 ? <HeatmapChart data={sortedData} xKey={rowDims[0]} yKey={rowDims[1] || colDim} valueKey={vKeys[0]} height={340} /> : <p className="text-xs text-muted-foreground text-center py-8">Heatmap needs 2+ dimensions</p>;
    case 'geo_map': {
      // Points / heatmap always render on a MapLibre basemap. Choropleth uses
      // the vector map by default, or MapLibre (satellite/terrain/streets) when
      // a real basemap is chosen.
      const gmode = cfg.geoMode || 'choropleth';
      const bm = cfg.basemap;
      const gscope = cfg.geoScope;
      if (gmode === 'points' || gmode === 'heatmap') {
        return <MapLibreGeoChart data={sortedData} locationKey={xKey} valueKey={vKeys[0]} height={400} config={cfg} formatValue={formatValue} onHover={onHover} />;
      }
      // County/city scopes and satellite/terrain basemaps use MapLibre, which
      // has the boundary-loading + FIPS index needed for county choropleths.
      if (gmode === 'choropleth' && (gscope === 'us-counties' || gscope === 'us-cities' || (bm && bm !== 'vector'))) {
        return <MapLibreGeoChart data={sortedData} locationKey={xKey} valueKey={vKeys[0]} height={400} config={cfg} formatValue={formatValue} onHover={onHover} />;
      }
      return <GeoMapChart data={sortedData} locationKey={xKey} valueKey={vKeys[0]} height={400} config={cfg} formatValue={formatValue} onHover={onHover} />;
    }
    default: {
      // Fall through to plugin registry for any custom-registered viz type
      const reg = getRegisteredVizType(vizType);
      if (reg && reg.renderer) {
        const Custom = reg.renderer;
        return <Custom data={sortedData} rowDims={rowDims} colDim={colDim} vKeys={vKeys} xKey={xKey} config={cfg} colors={colors} formatValue={formatValue} onDrill={onDrill} height={340} />;
      }
      return null;
    }
  }
  })();
  // One-click grouped↔stacked toggle for multi-series bar charts.
  const _isStacked = cfg.barLayout === 'stacked' || cfg.barLayout === 'percent';
  const _showStackToggle = vizType === 'bar_chart' && vKeys.length > 1 && typeof onToggleStack === 'function';
  return (
    <div ref={_setWrapEl} onContextMenu={handleDataPointContextMenu} style={{ width: '100%', height: '100%', minHeight: 0, position: 'relative' }}>
      {_showStackToggle && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleStack(_isStacked ? 'grouped' : 'stacked'); }}
          className="absolute top-1 right-1 z-10 text-[9px] px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors"
          title={_isStacked ? 'Switch to grouped bars' : 'Switch to stacked bars'}
          data-testid="bar-stack-toggle"
        >
          {_isStacked ? 'Stacked' : 'Grouped'}
        </button>
      )}
      {_viz}
      {dpMenu && (
        <div className="fixed inset-0 z-[200]" onClick={() => setDpMenu(null)} onContextMenu={(e) => { e.preventDefault(); setDpMenu(null); }}>
          <div className="glass-card rounded-lg shadow-2xl border border-border/50 py-1 w-56" style={{ position: 'fixed', left: Math.min(dpMenu.x, window.innerWidth - 240), top: Math.min(dpMenu.y, window.innerHeight - 100), zIndex: 201 }} onClick={(e) => e.stopPropagation()}>
            <button onClick={openAlertFromPoint} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30 transition-colors" data-testid="ctx-create-alert-point">
              <Bell className="w-3 h-3 text-amber-400" /> Create alert for {_labelOf(dpMenu.dimField)} = {String(dpMenu.dimValue)}
            </button>
            {_canEditModel && dpMenu.isCalc && (
              <button onClick={editMeasureInModel} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30 transition-colors" data-testid="ctx-edit-measure">
                <Pencil className="w-3 h-3 text-muted-foreground" /> Edit measure “{_labelOf(dpMenu.metric)}”
              </button>
            )}
          </div>
        </div>
      )}
      <Dialog open={!!alertForm} onOpenChange={(o) => { if (!o) setAlertForm(null); }}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="alert-from-point-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Bell className="w-4 h-4 text-amber-400" /> Create Alert</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Create an alert for the selected data point</DialogDescription>
          {alertForm && (
            <div className="space-y-3 mt-1">
              <div><label className="text-xs text-muted-foreground">Alert Name</label><input value={alertForm.name} onChange={e => setAlertForm(p => ({ ...p, name: e.target.value }))} className="w-full rounded-lg bg-muted/50 border border-border/50 mt-1 text-foreground text-sm px-2 py-1.5 outline-none" data-testid="point-alert-name" /></div>
              <div className="p-2.5 rounded-lg bg-primary/5 border border-primary/20 text-[11px]" data-testid="point-alert-condition">
                <p className="text-muted-foreground">Condition: <span className="text-foreground font-medium">{alertForm._dimLabel} = {String(alertForm.dimValue)}</span></p>
                <p className="text-muted-foreground">Metric: <span className="text-foreground font-medium">{alertForm._metricLabel}</span>{typeof alertForm._current === 'number' ? <span className="text-muted-foreground"> (now {alertForm._current.toLocaleString()})</span> : null}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs text-muted-foreground">When value is</label>
                  <select value={alertForm.operator} onChange={e => setAlertForm(p => ({ ...p, operator: e.target.value }))} className="w-full rounded-lg bg-muted/50 border border-border/50 mt-1 text-foreground text-sm px-2 py-1.5 outline-none">
                    <option value="gt">Greater than</option><option value="lt">Less than</option><option value="gte">≥</option><option value="lte">≤</option>
                  </select>
                </div>
                <div><label className="text-xs text-muted-foreground">Threshold</label><input type="number" value={alertForm.threshold} onChange={e => setAlertForm(p => ({ ...p, threshold: e.target.value }))} className="w-full rounded-lg bg-muted/50 border border-border/50 mt-1 text-foreground text-sm px-2 py-1.5 outline-none" data-testid="point-alert-threshold" /></div>
              </div>
              <div><label className="text-xs text-muted-foreground">Recipients</label><input value={alertForm.recipients} onChange={e => setAlertForm(p => ({ ...p, recipients: e.target.value }))} placeholder="email@company.com" className="w-full rounded-lg bg-muted/50 border border-border/50 mt-1 text-foreground text-sm px-2 py-1.5 outline-none" /></div>
              <button onClick={submitPointAlert} disabled={!alertForm.threshold} className="w-full rounded-lg bg-primary text-primary-foreground py-2 text-sm disabled:opacity-50" data-testid="confirm-point-alert"><Bell className="w-4 h-4 mr-1 inline" /> Create Alert</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

function DrillBreadcrumb({ path, rowDims, onNavigate, allFields, formatValue }) {
  const label = (dim) => getFieldLabel((allFields || []).find(f => f.name === dim), dim);
  const nextDim = rowDims[Math.min(path.length + 1, rowDims.length - 1)];
  return (
    <div className="flex items-center gap-1 px-1 pb-1 text-[10px] flex-wrap" data-testid="drill-breadcrumb">
      <button onClick={() => onNavigate([])} className={`px-1.5 py-0.5 rounded ${path.length === 0 ? 'text-foreground font-semibold' : 'text-primary hover:bg-primary/10'}`} data-testid="drill-crumb-root">{label(rowDims[0])}</button>
      {path.map((val, i) => (
        <React.Fragment key={i}>
          <span className="text-muted-foreground/70">›</span>
          <button onClick={() => onNavigate(path.slice(0, i + 1))} className={`px-1.5 py-0.5 rounded ${i === path.length - 1 ? 'text-foreground font-semibold' : 'text-primary hover:bg-primary/10'}`} data-testid={`drill-crumb-${i}`}>{formatValue ? formatValue(val, rowDims[i]) : String(val)}</button>
        </React.Fragment>
      ))}
      {path.length < rowDims.length - 1 && (
        <span className="text-muted-foreground/70 ml-1">· click a bar to drill into {label(nextDim)}</span>
      )}
    </div>
  );
}

function DateDefaultChip({ interp }) {
  if (!interp || !interp.was_defaulted) return null;
  return (
    <div
      className="absolute top-1 right-1 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-violet-500/15 border border-violet-500/30 text-violet-300 text-[9px] font-medium pointer-events-auto"
      title={interp.hint || `Interpreted the date as ${interp.used_field_label || interp.used_field} (model default).`}
      data-testid="date-default-chip"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
      {interp.used_field_label || interp.used_field}
    </div>
  );
}

export const ChartRenderer = React.memo(function ChartRenderer(props) {
  const { vizConfig, rowDims, vizType, allFields, formatValue } = props;
  const cfg = vizConfig || {};
  const dateInterp = cfg.dateInterpretation;
  const hasHierarchicalX = (rowDims?.length || 0) >= 2;
  const drilldownEnabled = !!cfg.drilldownHierarchy && hasHierarchicalX && DRILL_VIZ.has(vizType);
  const [drillPath, setDrillPath] = React.useState([]);
  const sig = (rowDims || []).join('|');
  React.useEffect(() => { setDrillPath([]); }, [sig, drilldownEnabled]);

  if (!drilldownEnabled) {
    if (dateInterp?.was_defaulted) {
      return (
        <div className="relative h-full">
          <DateDefaultChip interp={dateInterp} />
          <ChartRendererInner {...props} />
        </div>
      );
    }
    return <ChartRendererInner {...props} />;
  }
  return (
    <div className="relative h-full flex flex-col" data-testid="chart-drilldown-wrapper">
      <DateDefaultChip interp={dateInterp} />
      <DrillBreadcrumb path={drillPath} rowDims={rowDims} onNavigate={setDrillPath} allFields={allFields} formatValue={formatValue} />
      <div className="flex-1 min-h-0">
        <ChartRendererInner {...props} drillPath={drillPath} onDrillDown={(v) => setDrillPath(p => [...p, v])} />
      </div>
    </div>
  );
});
